import { get } from 'lodash';

import { useEffect } from 'react';
import { Control, useFieldArray } from 'react-hook-form';

import { Add16, Bin16 } from '@ingrid/iconography/react';
import { Button, FormItem, IconButton, ListLayout, TextInput } from '@ingrid/luterkort-react';

import { ValidationError } from '~src/components/ValidationError/ValidationError';
import { useTranslation } from '~src/i18n/use-translation';

import { DynamicPath, useCarrierContractFormContext } from '../../types';
import { useTranslatedFormLabel, useUISchema } from '../hooks';
import { Schema } from '../queries';
import { SchemaFormLabel } from './schema-form-label';

interface Props {
  schemaId: string;
  schema: Schema;
  path?: string;
}

type ArrayItemProps = Props & {
  onRemove: () => void;
  index: number;
};

function ArrayItem({ schema, schemaId, index, onRemove, path = '' }: ArrayItemProps) {
  const { formState, register } = useCarrierContractFormContext();
  const { isOptional } = useUISchema({ path, schemaId });
  const { t } = useTranslation();

  const label = useTranslatedFormLabel({ path, options: { index: index + 1 } });

  return (
    <div style={{ gap: '8px', display: 'flex', flexDirection: 'column' }}>
      <ListLayout.Stack css={{ alignItems: 'flex-end', flexDirection: 'row', gap: '16px', width: '100%' }}>
        <FormItem.Root style={{ width: '100%' }}>
          <SchemaFormLabel options={{ index: index + 1 }} path={path} schemaId={schemaId} schema={schema} />

          <FormItem.Control invalid={!!get(formState.errors, `${path}.${index}` as DynamicPath)?.message}>
            <TextInput
              {...register(
                `${path}.${index}` as DynamicPath,
                !isOptional
                  ? {
                      required: t('common.fieldRequired', {
                        field: label,
                      }),
                    }
                  : {}
              )}
            />
          </FormItem.Control>
        </FormItem.Root>

        <IconButton
          aria-label={t('common.removeItem', { item: `${path}.${index}` })}
          onClick={onRemove}
          style={{ flexShrink: '0' }}
          disabled={index === 0}
          variant="secondary"
        >
          <Bin16 />
        </IconButton>
      </ListLayout.Stack>

      <ValidationError errors={formState.errors} name={`${path}.${index}`} />
    </div>
  );
}

export function SchemaFormArray({ schema, schemaId, path = '' }: Props) {
  const { control, watch } = useCarrierContractFormContext();
  const { t } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control: control as unknown as Control<any>,
    name: path,
  });

  const lastValue = watch(`${path}.${fields.length - 1}` as DynamicPath);

  useEffect(() => {
    if (!fields.length) {
      append('');
    }
  }, [append, fields]);

  function handleAddClicked() {
    append('');
  }

  if (schema.type !== 'array') {
    return null;
  }

  return (
    <ListLayout.Column as="ol" style={{ display: 'flex', gap: '16px' }}>
      {fields.map(({ id }, index) => (
        <ArrayItem
          onRemove={() => remove(index)}
          schemaId={schemaId}
          schema={schema}
          index={index}
          path={path}
          key={id}
        />
      ))}

      <div>
        <Button onClick={handleAddClicked} variant="secondary" disabled={!lastValue && !!fields.length}>
          <Add16 />
          {t('common.add')}
        </Button>
      </div>
    </ListLayout.Column>
  );
}
